import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarDays } from "lucide-react";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { Calendar } from "../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { useC } from "../../context/SiteData";
import { API } from "../../lib/api";


export const RandevuForm = () => {
  const c = useC();
  const slots = c.time_slots.split(",").map((s) => s.trim()).filter(Boolean);
  const [form, setForm] = useState({ name: "", phone: "", date: "", time_slot: slots[0] || "", note: "" });
  const [sending, setSending] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.date) {
      toast.error("Lütfen randevu için bir gün seçin");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/appointments`, form);
      toast.success("Randevu talebiniz alındı. Onay için size dönüş yapacağız.");
      setForm({ name: "", phone: "", date: "", time_slot: slots[0] || "", note: "" });
    } catch {
      toast.error("Randevu gönderilemedi. Lütfen telefon veya WhatsApp ile deneyin.");
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={onSubmit} data-testid="randevu-form" className="border border-line bg-white p-7 sm:p-10">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center bg-ink-900 text-cream-50">
          <CalendarDays size={18} />
        </span>
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-ink-900">{c.randevu_title}</h2>
          <p className="mt-1 text-sm text-ink-500">{c.randevu_text}</p>
        </div>
      </div>
      <div className="mt-8 grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="r-name" className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-500">Adınız Soyadınız *</label>
          <input id="r-name" name="name" required value={form.name} onChange={onChange} data-testid="randevu-input-name" className="input-field" placeholder="Ad Soyad" />
        </div>
        <div>
          <label htmlFor="r-phone" className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-500">Telefon *</label>
          <input id="r-phone" name="phone" type="tel" required value={form.phone} onChange={onChange} data-testid="randevu-input-phone" className="input-field" placeholder="05__ ___ __ __" />
        </div>
        <div>
          <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-500">Tarih *</label>
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="randevu-input-date"
                className={`input-field w-full text-left ${!form.date ? "text-ink-400" : ""}`}
              >
                {form.date ? format(new Date(form.date + "T12:00:00"), "d MMMM yyyy, EEEE", { locale: tr }) : "Gün seçin"}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto border-line bg-white p-0" align="start">
              <Calendar
                mode="single"
                locale={tr}
                selected={form.date ? new Date(form.date + "T12:00:00") : undefined}
                onSelect={(d) => d && setForm((f) => ({ ...f, date: format(d, "yyyy-MM-dd") }))}
                disabled={{ before: new Date() }}
                data-testid="randevu-calendar"
              />
            </PopoverContent>
          </Popover>
        </div>
        <div>
          <label htmlFor="r-slot" className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-500">Saat Aralığı *</label>
          <select id="r-slot" name="time_slot" value={form.time_slot} onChange={onChange} data-testid="randevu-input-slot" className="input-field">
            {slots.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="r-note" className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-500">Notunuz</label>
          <textarea id="r-note" name="note" rows={3} value={form.note} onChange={onChange} data-testid="randevu-input-note" className="input-field resize-none" placeholder="Eklemek istedikleriniz" />
        </div>
      </div>
      <button type="submit" disabled={sending} data-testid="randevu-submit" className="btn-primary mt-8 w-full disabled:opacity-60 sm:w-auto">
        {sending ? "Gönderiliyor..." : "Randevu Talebi Gönder"}
      </button>
    </form>
  );
};
